// ========================================
// CLIENT-SIDE LOGGER SERVICE
// ========================================
// Production-safe logging service for client-side operations
// - Sanitizes sensitive data in production
// - Environment-based log levels
// - Structured logging for monitoring services

export type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'security'

export interface LogEntry {
  level: LogLevel
  message: string
  timestamp: string
  data?: unknown
  source?: string
}

interface LogContext {
  [key: string]: unknown
}

class Logger {
  private isDevelopment: boolean
  private enableConsoleLogging: boolean
  private enablePerformanceLogs: boolean
  private logLevel: LogLevel

  constructor() {
    this.isDevelopment = import.meta.env.DEV === true

    // Check environment variables for logging controls
    this.enableConsoleLogging =
      import.meta.env.VITE_ENABLE_CONSOLE_LOGGING !== 'false'
    this.enablePerformanceLogs =
      import.meta.env.VITE_ENABLE_PERFORMANCE_LOGS !== 'false'
    this.logLevel = (import.meta.env.VITE_LOG_LEVEL as LogLevel) || 'info'
  }

  /**
   * Sanitize error objects to remove sensitive data
   */
  private sanitizeError(error: unknown): unknown {
    if (error instanceof Error) {
      return {
        message: error.message,
        name: error.name,
        code: (error as Error & { code?: string }).code, // Firebase error codes
        // NO stack traces in production
        ...(this.isDevelopment && { stack: error.stack }),
      }
    }
    return error
  }

  /**
   * Sanitize context data to remove PII and sensitive fields
   */
  private sanitizeContext(context: LogContext): LogContext {
    if (this.isDevelopment) {
      return context // Full context in development
    }

    const sanitized: LogContext = {}
    const sensitiveFields = [
      'email',
      'password',
      'token',
      'secret',
      'key',
      'credential',
    ]

    for (const [key, value] of Object.entries(context)) {
      // Check if key contains sensitive terms
      if (sensitiveFields.some(field => key.toLowerCase().includes(field))) {
        sanitized[key] = '[REDACTED]'
      } else if (key === 'uid' && typeof value === 'string') {
        // Hash UIDs in production for correlation without exposing actual IDs
        sanitized[key] = value.substring(0, 8) + '...'
      } else if (
        value &&
        typeof value === 'object' &&
        'message' in value &&
        'name' in value
      ) {
        // Sanitize error objects
        sanitized[key] = this.sanitizeError(value)
      } else {
        sanitized[key] = value
      }
    }

    return sanitized
  }

  private shouldLog(level: LogLevel, source?: string): boolean {
    // Always log security events
    if (level === 'security') {
      return true
    }

    // Check if logging is disabled entirely
    if (!this.enableConsoleLogging && !this.isDevelopment) {
      return false
    }

    // Check if performance logs are disabled and this is a performance log
    if (!this.enablePerformanceLogs && source === 'performance') {
      return false
    }

    // Always log errors and warnings regardless of level settings
    if (level === 'error' || level === 'warn') {
      return true
    }

    // Check log level hierarchy
    const levels: LogLevel[] = ['debug', 'info', 'warn', 'error', 'security']
    const currentLevelIndex = levels.indexOf(this.logLevel)
    const messageLevelIndex = levels.indexOf(level)

    return messageLevelIndex >= currentLevelIndex
  }

  private log(
    level: LogLevel,
    message: string,
    data?: unknown,
    source?: string
  ): void {
    if (!this.shouldLog(level, source)) {
      return
    }

    // Sanitize data if it's an object (context)
    const sanitizedData =
      data && typeof data === 'object'
        ? this.sanitizeContext(data as LogContext)
        : data

    const entry: LogEntry = {
      level,
      message,
      timestamp: new Date().toISOString(),
      data: sanitizedData,
      source,
    }

    this.output(entry)

    // TODO: Send to external monitoring service in production
    // if (!this.isDevelopment && (level === 'error' || level === 'security')) {
    //   sendToMonitoring(entry);
    // }
  }

  private output(entry: LogEntry): void {
    const { level, message, timestamp, data, source } = entry
    const prefix = `[${timestamp}] ${level.toUpperCase()}${source ? ` [${source}]` : ''}:`

    switch (level) {
      case 'security':
        // Security events use warn channel for visibility
        console.warn(prefix, message, data || '')
        break
      case 'error':
        console.error(prefix, message, data || '')
        break
      case 'warn':
        console.warn(prefix, message, data || '')
        break
      case 'info':
        console.info(prefix, message, data || '')
        break
      case 'debug':
        console.debug(prefix, message, data || '')
        break
    }
  }

  debug(message: string, data?: unknown, source?: string): void {
    this.log('debug', message, data, source)
  }

  info(message: string, data?: unknown, source?: string): void {
    this.log('info', message, data, source)
  }

  warn(message: string, data?: unknown, source?: string): void {
    this.log('warn', message, data, source)
  }

  error(
    message: string,
    errorOrData?: unknown,
    contextOrSource?: unknown
  ): void {
    // Support both signatures:
    // error(message, error, context) - new security-aware signature
    // error(message, data, source) - legacy signature
    if (
      errorOrData instanceof Error ||
      (errorOrData &&
        typeof errorOrData === 'object' &&
        'message' in errorOrData)
    ) {
      // New signature: error(message, error, context)
      const context =
        contextOrSource && typeof contextOrSource === 'object'
          ? contextOrSource
          : {}
      this.log('error', message, {
        ...context,
        error: this.sanitizeError(errorOrData),
      })
    } else {
      // Legacy signature: error(message, data, source)
      this.log(
        'error',
        message,
        errorOrData,
        typeof contextOrSource === 'string' ? contextOrSource : undefined
      )
    }
  }

  security(event: string, context?: LogContext): void {
    this.log('security', event, context)
  }
}

// Export singleton instance
export const logger = new Logger()
